import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI, topicsAPI, subscriptionAPI, trendsAPI, platformStatsAPI } from '../services/apiEndpoints';
import TopBar from './TopBar';
import MilestoneCelebration, { getMilestoneToCelebrate } from './MilestoneCelebration';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { useContentVertex } from '../contexts/ContentVertexContext';
import {
    LightningBoltIcon,
    RocketIcon,
    FileTextIcon,
    CheckCircledIcon,
    ClockIcon,
    ExclamationTriangleIcon,
    ReaderIcon,
    PaperPlaneIcon,
    BarChartIcon,
} from '@radix-ui/react-icons';

// ─── helpers ────────────────────────────────────────────────────────────────

const NICHE_GREETING = {
    affiliate:  (n) => `Ready to publish your next review, ${n}?`,
    blog:       (n) => `What will you create today, ${n}?`,
    news:       (n) => `What's the story today, ${n}?`,
    ecommerce:  (n) => `Ready to drive more traffic, ${n}?`,
    agency:     (n) => `Let's deliver for your clients today, ${n}.`,
    other:      (n) => `Good to see you, ${n}. Let's win today.`,
};

const TIER_LABELS = { creator: 'Creator', pro: 'Pro', elite: 'Elite' };

const StatCard = ({ label, value, sub, icon: Icon, iconBg, atRisk }) => (
    <div className={`rounded-2xl bg-zinc-900/60 border p-5 flex items-start justify-between gap-3 transition-all duration-300
        ${atRisk ? 'border-amber-500/40 animate-pulse-slow' : 'border-white/[0.07] hover:border-white/[0.14]'}`}>
        <div className="min-w-0">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-zinc-500 mb-2">{label}</p>
            <p className="text-2xl font-bold tabular-nums text-zinc-100 leading-none">{value}</p>
            {sub && <p className="text-xs text-zinc-500 mt-1.5 leading-snug">{sub}</p>}
        </div>
        <div className={`p-2.5 rounded-xl flex-shrink-0 ${iconBg}`}>
            {Icon ? <Icon className="h-5 w-5" /> : null}
        </div>
    </div>
);

// ─── component ───────────────────────────────────────────────────────────────

const Dashboard = () => {
    let user = null;
    try { user = JSON.parse(localStorage.getItem('user') || 'null'); } catch { /* corrupt localStorage */ }
    const { pendingCount, readyToPublishCount, onGenerateClick, niche } = useContentVertex();

    const [stats, setStats]               = useState(null);
    const [articles, setArticles]         = useState([]);
    const [activityFeed, setActivityFeed] = useState([]);
    const [platformStats, setPlatformStats] = useState(null);
    const [milestone, setMilestone]       = useState(null);
    const [trendingTopics, setTrendingTopics] = useState([]);
    const [loading, setLoading]           = useState(true);

    const navigate = useNavigate();

    useEffect(() => {
        document.title = pendingCount > 0 ? `ContentVertex (${pendingCount} pending)` : 'ContentVertex';
        return () => { document.title = 'ContentVertex'; };
    }, [pendingCount]);

    useEffect(() => {
        loadAll();
        const interval = setInterval(loadActivityFeed, 10_000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        loadPlatformStats();
        const t = setInterval(loadPlatformStats, 300_000);
        return () => clearInterval(t);
    }, []);

    const loadAll = async () => {
        try {
            setLoading(true);
            await Promise.all([loadStats(), loadArticles(), loadActivityFeed(), loadTrendingTopics()]);
        } finally {
            setLoading(false);
        }
    };

    const loadStats = async () => {
        try {
            const res = await dashboardAPI.getStats();
            const s = res.data.stats || res.data;
            setStats(s);
            if (s?.total_articles != null) {
                const m = getMilestoneToCelebrate(s.total_articles);
                if (m) setMilestone(m);
            }
        } catch { /* non-critical */ }
    };

    const loadPlatformStats = async () => {
        try {
            const res = await platformStatsAPI.get();
            setPlatformStats(res.data);
        } catch { /* non-critical */ }
    };

    const loadArticles = async () => {
        try {
            const res = await topicsAPI.getTopics();
            const data = res.data.data || res.data.topics || res.data || [];
            setArticles(
                Array.isArray(data)
                    ? data.filter(a => a.status === 'published' || a.status === 'completed' || a.generated_title).slice(0, 8)
                    : []
            );
        } catch { /* non-critical */ }
    };

    const loadActivityFeed = async () => {
        try {
            const res = await dashboardAPI.getActivityLog();
            const activities = res.data.activities || res.data || [];
            setActivityFeed(activities.map(a => ({
                message:  a.message || a.title,
                status:   a.status,
                time_ago: getRelativeTime(a.updated_at || a.created_at),
            })));
        } catch { /* non-critical */ }
    };

    const loadTrendingTopics = async () => {
        try {
            const res = await trendsAPI.discoverTrending('content marketing', '2840', false, false);
            setTrendingTopics((res.data?.trending_topics || res.data?.topics || []).slice(0, 3));
        } catch { /* non-critical */ }
    };

    const getRelativeTime = (ds) => {
        if (!ds) return 'Just now';
        const d = Math.floor((Date.now() - new Date(ds)) / 1000);
        if (d < 60)    return 'Just now';
        if (d < 3600)  return `${Math.floor(d / 60)}m ago`;
        if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
        return `${Math.floor(d / 86400)}d ago`;
    };

    const getActivityIcon = (msg = '') => {
        const m = msg.toLowerCase();
        if (m.includes('published') || m.includes('completed') || m.includes('success')) return CheckCircledIcon;
        if (m.includes('processing') || m.includes('generating') || m.includes('queued')) return ClockIcon;
        if (m.includes('failed') || m.includes('error')) return ExclamationTriangleIcon;
        return FileTextIcon;
    };

    // Derived
    const firstName  = user?.name?.split(' ')[0] || 'there';
    const greetingFn = NICHE_GREETING[niche] || ((n) => `Good to see you, ${n}.`);
    const greeting   = greetingFn(firstName);

    const normComparison = (() => {
        if (!platformStats || !stats) return null;
        const userW = stats.articles_this_week ?? 0;
        const avg   = platformStats.avg_weekly_articles_per_creator ?? 0;
        if (avg === 0) return null;
        return { isAbove: userW >= avg, userWeekly: userW, avg, pct: Math.min(Math.round((userW / Math.max(1, avg)) * 50), 99) };
    })();

    const tierProgressPct = stats?.next_tier_threshold
        ? Math.min(100, Math.round(((stats.total_articles ?? 0) / stats.next_tier_threshold) * 100))
        : 0;

    const statusColors = {
        published:    'text-emerald-400 bg-emerald-500/10 border-emerald-500/20',
        completed:    'text-blue-400 bg-blue-500/10 border-blue-500/20',
        needs_review: 'text-amber-400 bg-amber-500/10 border-amber-500/20',
        processing:   'text-violet-400 bg-violet-500/10 border-violet-500/20',
        failed:       'text-red-400 bg-red-500/10 border-red-500/20',
    };

    return (
        <div className="text-zinc-50 bg-zinc-950">
            {/* Milestone overlay */}
            {milestone && <MilestoneCelebration milestone={milestone} onDismiss={() => setMilestone(null)} />}

            <main className="min-h-screen flex flex-col">

                {/* Top header — flex-shrink-0, never scrolls away */}
                <div className="flex-shrink-0 px-7 pt-6 pb-4 border-b border-white/[0.05]">
                    <TopBar title="Dashboard" onGenerateClick={onGenerateClick} />

                    {/* Greeting + streak pill — Law 1: Obvious */}
                    <div className="flex flex-wrap items-center gap-3 mt-3">
                        <h1 className="text-xl font-semibold text-zinc-200">{greeting}</h1>
                        {stats?.current_streak > 0 && (
                            <span className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border
                                ${stats.streak_at_risk
                                    ? 'text-amber-300 bg-amber-500/15 border-amber-500/30 animate-pulse-slow'
                                    : 'text-amber-400 bg-amber-500/10 border-amber-500/20'
                                }`}>
                                🔥 {stats.current_streak}-day streak{stats.streak_at_risk ? ' — publish today!' : ''}
                            </span>
                        )}
                        {pendingCount > 0 && (
                            <span className="flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold border text-violet-300 bg-violet-500/10 border-violet-500/20">
                                <ClockIcon className="h-3 w-3" />
                                {pendingCount} generating
                            </span>
                        )}
                    </div>
                </div>

                {/* ── Scrollable content ─────────────────────────────────── */}
                <div className="flex-1 overflow-y-auto custom-scrollbar">
                    <div className="px-7 py-6 space-y-6">

                        {/* Ready-to-publish CTA — Law 3: Easy */}
                        {readyToPublishCount > 0 && (
                            <div className="flex items-center justify-between gap-4 px-5 py-4 rounded-2xl bg-gradient-to-r from-emerald-500/10 to-teal-500/5 border border-emerald-500/25">
                                <div className="flex items-center gap-3 min-w-0">
                                    <div className="p-2 rounded-xl bg-emerald-500/15 flex-shrink-0">
                                        <PaperPlaneIcon className="h-4 w-4 text-emerald-400" />
                                    </div>
                                    <div className="min-w-0">
                                        <p className="text-sm font-semibold text-emerald-300">
                                            {readyToPublishCount} article{readyToPublishCount !== 1 ? 's' : ''} ready to publish
                                        </p>
                                        <p className="text-xs text-zinc-500 mt-0.5">Don't let them sit — every day offline is traffic lost</p>
                                    </div>
                                </div>
                                <Button
                                    size="sm"
                                    className="flex-shrink-0 bg-emerald-600 hover:bg-emerald-500 text-white border-0 font-semibold"
                                    onClick={() => navigate('/topic-queue')}
                                >
                                    Review &amp; Publish →
                                </Button>
                            </div>
                        )}

                        {/* ── Stats grid — Law 4: Satisfying ─────────────── */}
                        <div className="grid grid-cols-2 xl:grid-cols-4 gap-4">
                            <StatCard
                                label="Time Saved"
                                value={stats?.working_days_saved ? `${stats.working_days_saved}d` : `${stats?.hours_saved?.toFixed(1) || '0.0'}h`}
                                sub={stats?.working_days_saved ? `${stats.hours_saved?.toFixed(1) || 0}h of writing` : `${stats?.total_articles ?? 0} articles generated`}
                                icon={ClockIcon}
                                iconBg="bg-blue-500/10 text-blue-400"
                            />
                            <StatCard
                                label="Est. Traffic Value"
                                value={`$${stats?.traffic_value?.toLocaleString() || '0'}`}
                                sub="Growing with every article"
                                icon={() => (
                                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                )}
                                iconBg="bg-emerald-500/10 text-emerald-400"
                            />
                            <StatCard
                                label="Words Published"
                                value={(stats?.total_words ?? 0).toLocaleString()}
                                sub={stats?.avg_seo_score ? `Avg quality ${stats.avg_seo_score}/100` : 'Indexing in search engines'}
                                icon={ReaderIcon}
                                iconBg="bg-violet-500/10 text-violet-400"
                            />
                            <StatCard
                                label="Publishing Streak"
                                value={`${stats?.current_streak ?? 0}`}
                                sub={stats?.streak_at_risk ? '⚠ Publish today to keep it!' : `Longest: ${stats?.longest_streak ?? 0} days`}
                                icon={() => <span className={`text-xl leading-none ${stats?.streak_at_risk ? 'animate-bounce' : ''}`}>🔥</span>}
                                iconBg={stats?.streak_at_risk ? 'bg-amber-500/20' : 'bg-amber-500/10'}
                                atRisk={stats?.streak_at_risk}
                            />
                        </div>

                        {/* ── Next-tier progress — Law 4: Satisfying ─────── */}
                        {stats?.next_tier && stats?.next_tier_threshold && (
                            <div className="rounded-2xl bg-zinc-900/60 border border-white/[0.07] px-5 py-4 space-y-2">
                                <div className="flex justify-between items-center">
                                    <p className="text-xs font-semibold uppercase tracking-widest text-zinc-500">
                                        Progress to {TIER_LABELS[stats.next_tier] || stats.next_tier} tier
                                    </p>
                                    <span className="text-xs text-zinc-500 tabular-nums">
                                        {stats.total_articles ?? 0} / {stats.next_tier_threshold} articles
                                    </span>
                                </div>
                                <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-gradient-to-r from-violet-500 to-indigo-500 rounded-full transition-all duration-700"
                                        style={{ width: `${tierProgressPct}%` }}
                                    />
                                </div>
                                <p className="text-[11px] text-zinc-600">
                                    {stats.next_tier_articles_needed} more article{stats.next_tier_articles_needed !== 1 ? 's' : ''} to unlock {TIER_LABELS[stats.next_tier] || stats.next_tier} Creator
                                    {stats.avg_seo_score ? ` · Avg quality ${stats.avg_seo_score}/100` : ''}
                                </p>
                            </div>
                        )}

                        {/* ── Norm comparison — compact banner ────────────── */}
                        {normComparison && (
                            <div className={`flex items-center justify-between gap-4 px-5 py-3 rounded-2xl border text-sm
                                ${normComparison.isAbove
                                    ? 'bg-emerald-500/[0.06] border-emerald-500/20 text-emerald-300'
                                    : 'bg-white/[0.03] border-white/[0.07] text-zinc-400'
                                }`}>
                                <p className="leading-relaxed">
                                    {normComparison.isAbove
                                        ? <>🔥 You're publishing more than <strong className="text-emerald-200">{normComparison.pct}%</strong> of ContentVertex creators this week.</>
                                        : <>Creators publish <strong className="text-zinc-200">{normComparison.avg} articles/week</strong> on average. You've published <strong className="text-zinc-200">{normComparison.userWeekly}</strong> this week.</>
                                    }
                                </p>
                                <Button
                                    size="sm"
                                    onClick={onGenerateClick}
                                    className={`flex-shrink-0 border-0 text-white font-semibold
                                        ${normComparison.isAbove
                                            ? 'bg-emerald-600 hover:bg-emerald-500'
                                            : 'bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-500 hover:to-blue-500'
                                        }`}
                                >
                                    {normComparison.isAbove ? 'Generate Another →' : 'Generate Now →'}
                                </Button>
                            </div>
                        )}

                        {/* ── Main 2-col: content table + activity feed ──── */}
                        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">

                            {/* Top Performing Content */}
                            <div className="xl:col-span-2 rounded-2xl bg-zinc-900/60 border border-white/[0.07] overflow-hidden">
                                <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.06]">
                                    <div className="flex items-center gap-2">
                                        <RocketIcon className="h-4 w-4 text-emerald-400" />
                                        <h2 className="text-sm font-semibold text-zinc-200">Top Performing Content</h2>
                                    </div>
                                </div>

                                {loading ? (
                                    <div className="flex items-center justify-center py-12 text-zinc-500 text-sm">
                                        <ClockIcon className="h-4 w-4 animate-spin mr-2" />
                                        Loading…
                                    </div>
                                ) : articles.length > 0 ? (
                                    <div className="divide-y divide-white/[0.05]">
                                        {articles.map((a, idx) => {
                                            const sc = statusColors[a.status] || 'text-zinc-400 bg-zinc-500/10 border-zinc-500/20';
                                            return (
                                                <div key={a.id} className="flex items-center gap-4 px-5 py-3 hover:bg-white/[0.03] transition-colors">
                                                    <span className="text-xs text-zinc-600 font-mono w-5 flex-shrink-0">{String(idx + 1).padStart(2, '0')}</span>
                                                    <div className="flex-1 min-w-0">
                                                        <p className="text-sm font-medium text-zinc-200 truncate">
                                                            {a.generated_title || a.topic_title || 'Untitled'}
                                                        </p>
                                                        {a.word_press_site?.site_name && (
                                                            <p className="text-xs text-zinc-500 mt-0.5 truncate">{a.word_press_site.site_name}</p>
                                                        )}
                                                    </div>
                                                    <span className="text-xs text-zinc-500 tabular-nums flex-shrink-0">
                                                        {a.word_count ? `${a.word_count.toLocaleString()}w` : '—'}
                                                    </span>
                                                    <span className={`flex-shrink-0 px-2 py-0.5 rounded border text-[10px] font-medium capitalize ${sc}`}>
                                                        {a.status?.replace('_', ' ')}
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="flex flex-col items-center justify-center py-12 gap-4">
                                        <div className="p-4 rounded-2xl bg-violet-500/10">
                                            <RocketIcon className="h-8 w-8 text-violet-400" />
                                        </div>
                                        <div className="text-center">
                                            <p className="text-sm font-semibold text-zinc-200">Your first article is one click away</p>
                                            <p className="text-xs text-zinc-500 mt-1">Your competitors are publishing daily. Start now.</p>
                                        </div>
                                        {trendingTopics.length > 0 && (
                                            <div className="flex flex-wrap gap-2 justify-center">
                                                {trendingTopics.map((t, i) => (
                                                    <button key={i} onClick={() => onGenerateClick?.(t.topic || t.title || t)}
                                                        className="px-3 py-1 rounded-full text-xs bg-white/[0.05] border border-white/10 text-zinc-300 hover:bg-violet-500/20 hover:border-violet-500/40 hover:text-violet-300 transition-all">
                                                        {t.topic || t.title || t}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                        <button onClick={() => onGenerateClick?.()}
                                            className="flex items-center gap-2 px-6 py-2.5 rounded-full bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-500 hover:to-blue-500 text-white font-semibold text-sm shadow-lg shadow-violet-500/25 transition-all hover:scale-105 active:scale-95">
                                            <LightningBoltIcon className="h-4 w-4 text-yellow-400" />
                                            Generate Your First Article
                                        </button>
                                    </div>
                                )}
                            </div>

                            {/* Activity Feed + Platform Stats */}
                            <div className="xl:col-span-1 flex flex-col gap-4">

                                {/* Platform stats pill */}
                                {platformStats && (
                                    <div className="rounded-2xl bg-zinc-900/60 border border-white/[0.07] px-5 py-4">
                                        <p className="text-[10px] font-semibold uppercase tracking-widest text-zinc-600 mb-3">Platform Activity</p>
                                        <div className="space-y-2">
                                            <div className="flex justify-between text-xs">
                                                <span className="text-zinc-500">Articles today</span>
                                                <span className="text-zinc-300 font-semibold tabular-nums">{platformStats.articles_generated_today?.toLocaleString()}</span>
                                            </div>
                                            <div className="flex justify-between text-xs">
                                                <span className="text-zinc-500">Active creators</span>
                                                <span className="text-zinc-300 font-semibold tabular-nums">{platformStats.total_active_creators?.toLocaleString()}</span>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Activity timeline */}
                                <div className="flex-1 rounded-2xl bg-zinc-900/60 border border-white/[0.07] overflow-hidden flex flex-col">
                                    <div className="flex items-center gap-2 px-5 py-4 border-b border-white/[0.06]">
                                        <span className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse flex-shrink-0" />
                                        <h3 className="text-sm font-semibold text-zinc-200">Activity</h3>
                                        <span className="ml-auto text-[10px] font-semibold uppercase tracking-widest text-zinc-600">Live</span>
                                    </div>
                                    <ScrollArea className="flex-1 max-h-72">
                                        <div className="px-4 py-3 space-y-3">
                                            {activityFeed.length > 0 ? activityFeed.map((item, idx) => {
                                                const Icon = getActivityIcon(item.message);
                                                const cols = ['text-violet-400', 'text-cyan-400', 'text-pink-400', 'text-emerald-400'];
                                                return (
                                                    <div key={idx} className="flex gap-3">
                                                        <div className={`p-1.5 rounded-lg bg-white/[0.05] ${cols[idx % cols.length]} flex-shrink-0`}>
                                                            <Icon className="h-3.5 w-3.5" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <p className="text-xs text-zinc-300 leading-snug">{item.message}</p>
                                                            <p className="text-[10px] text-zinc-600 mt-0.5">{item.time_ago}</p>
                                                        </div>
                                                    </div>
                                                );
                                            }) : (
                                                <p className="text-xs text-zinc-600 text-center py-6">No recent activity</p>
                                            )}
                                        </div>
                                    </ScrollArea>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default Dashboard;
